<template>
	<NuxtLayout
		name="hero-diagonal"
		:background-video="{
			mp4Src: cdnUrl('fit-hier-zwangerfit-header_1080p_v1.mp4'),
			webmSrc: cdnUrl('fit-hier-zwangerfit-header_1080p_v1.webm'),
			posterImg: {
				src: '/img/fitness/small-group-training_01.jpg',
				alt: 'Fit Hier MamaFit',
				preload: true,
				width: 1920,
				height: 1490,
			},
		}"
		page-title-prefix="MamaFit"
		page-title="Blijf fit tijdens én na jouw zwangerschap"
		:video-color="VideoColorEnum.RedPurple"
		:breadcrumbs
	>
		<div class="flex flex-col gap-36 pb-36 md:pt-64">
			<div class="flex flex-col gap-36 sticky">
				<div class="lg:container flex flex-col md:grid md:grid-cols-3 gap-12">
					<div>
						<div class="sticky top-36 flex flex-col gap-8 px-12 md:px-0">
							<AtomCard class="p-12 flex flex-col gap-12 rounded-lg">
								<div class="flex flex-col gap-4">
									<AtomTitle
										tag="h2"
										size="xl"
									>
										Het MamaFit programma
									</AtomTitle>
									<p class="text-functional-size-lg text-gray-600">
										Waarom Hier?
									</p>
								</div>
								<MoleculeIconList
									:items="[
										'Blijf fit tijdens en na de zwangerschap',
										'Train samen met andere vrouwen',
										'Professionele coaching',
										'Op maat voor jouw fase in de zwangerschap',
										'Voor een gezonde zwangerschap',
										'Motiverende en veilige omgeving',
										'Trainen in een discrete privé omgeving',
									]"
								/>
								<AtomButton
									:variant="ButtonVariant.Green"
									text="Informatiepakket aanvragen"
									size="lg"
									icon-right="fa-envelope"
									:to="{
										url: '#aanmelden',
										title: 'Aanmeldformulier MamaFit',
									}"
								/>
							</AtomCard>
						</div>
					</div>
					<div class="relative col-span-2 grid gap-12">
						<div class="col-span-2 flex flex-col gap-12 bg-gray-50 md:rounded-lg p-12">
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Fit en sterk door je zwangerschap
								</AtomTitle>
								<p class="text-paragraph-size-lg">
									Met het MamaFit programma werk je aan een gezonde conditie gedurende én na jouw zwangerschap. Zo bouw je veilig aan kracht en conditie om latere klachten te voorkomen en weer als voorheen te vertrouwen op je lichaam.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Het programma
								</AtomTitle>
								<p>
									Elke maand starten we met een nieuwe groep in het MamaFit programma. In dit programma ga je samen met een van onze trainers of fysiotherapeuten aan de slag om jouw lichaam in goede conditie te houden gedurende én na jouw zwangerschap.
								</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Wat kun je verwachten?
								</AtomTitle>
								<p>
									Elke training duurt 45 minuten en vindt plaats in onze kleinschalige, moderne studio. Je werkt samen met je trainer aan:
								</p>
								<MoleculeIconList
									:items="[
										'Persoonlijke doelstellingen en voortgang',
										'Correcte houding, ademhaling en techniek',
										'Variatie in oefeningen en trainingsvormen',
										'Metingen en evaluaties',
										'Ondersteuning bij leefstijl en herstel',
									]"
								/>
								<p>Gratis gebruik van handdoeken, materialen en koffiebar is inbegrepen. Wij verzorgen alles wat je nodig hebt, jij richt je volledig op je training.</p>
							</div>
							<div class="relative flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
									class="mt-8"
								>
									Waar ga je trainen?
								</AtomTitle>
								<p>
									De trainingen worden gegeven in een moderne, veilige en discrete omgeving. Altijd onder begeleiding van een trainer of fysiotherapeut. Zo train je op een veilige manier in een fijne, stimulerende en rustige setting.
								</p>
							</div>
						</div>
					</div>
				</div>

				<div class="container grid grid-cols-1 md:grid-cols-2 gap-8">
					<AtomImage
						src="/img/fitness/kettlebell_01.jpg"
						alt="Het MamaFit programma"
						:width="640"
						:height="480"
						img-class="rounded object-cover aspect-[4/3]"
					/>
					<AtomImage
						src="/img/fitness/resistance-band_01.jpg"
						alt="Het MamaFit programma"
						:width="640"
						:height="480"
						img-class="rounded object-cover aspect-[4/3]"
					/>
				</div>

				<MoleculeBackgroundSection
					id="aanmelden"
					color="pink"
					class="lg:scroll-mt-72"
				>
					<div
						class="flex flex-col items-center gap-12"
					>
						<AtomTitle
							tag="h2"
							size="3xl"
						>
							MamaFit informatiepakket
						</AtomTitle>
						<div class="lg:w-3/4 flex flex-col gap-12">
							<p class="text-center">
								Kom in beweging voor een gezonde zwangerschap en zorg voor je lichaam na je zwangerschap!
								Vraag jouw MamaFit informatiepakket aan door hieronder het formulier in te vullen. In het pakket vind je alle informatie over het programma, inclusief de tarieven. We nemen binnen 1 werkdag contact met je op.
							</p>
							<AtomCard class="p-12 flex flex-col gap-12 rounded-lg text-black">
								<OrganismFormLeadZft />
							</AtomCard>
						</div>
					</div>
				</MoleculeBackgroundSection>
			</div>

			<div class="flex flex-col gap-64">
				<div class="flex flex-col gap-24 scroll-mt-36">
					<div class="container flex flex-col gap-20">
						<div class="flex flex-col gap-6">
							<div class="flex flex-col gap-2">
								<AtomTitle
									tag="h2"
									size="2xl"
								>
									Trainen tijdens en na je zwangerschap
								</AtomTitle>
								<p class=" text-gray-600 text-functional-size-lg">
									Waarom is dit belangrijk?
								</p>
							</div>
							<p class="text-paragraph-size-lg">
								Door tijdens en na je zwangerschap te trainen houd je jouw lichaam in goede conditie. Dit is belangrijk om klachtenvrij te blijven in een periode waarin je lichaam veel fysieke verandering doormaakt.
							</p>
						</div>
					</div>
					<div class="container max-w-screen-2xl flex flex-col gap-12">
						<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Fysieke fases tijdens de zwangerschap
								</AtomTitle>
								<p>
									Tijdens je zwangerschap doorloop je drie hoofdfasen: het eerste trimester met hormonale veranderingen, het tweede trimester met groeiende buik en veranderend zwaartepunt, en het derde trimester met toenemende belasting op je lichaam. Elke fase brengt unieke fysieke uitdagingen met zich mee.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Mogelijke klachten die je ervaart
								</AtomTitle>
								<p>
									Veelvoorkomende klachten zijn rugpijn door de groeiende buik, bekkenpijn door hormonale veranderingen, vermoeidheid, en spierzwakte. Ook kun je last krijgen van een verminderde conditie en minder stabiliteit in je gewrichten.
								</p>
							</AtomCard>
							<AtomCard class="p-12 flex flex-col gap-8">
								<AtomTitle
									tag="h3"
									size="xl"
								>
									Waarom training hierbij helpt
								</AtomTitle>
								<p>
									Door gericht te trainen verbeter je je houding, versterk je je spieren, en vergroot je je stabiliteit. Dit helpt klachten te verminderen en bereidt je lichaam voor op de bevalling en het herstel daarna. Bovendien bevordert training je algehele welzijn en energielevel.
								</p>
							</AtomCard>
						</div>
					</div>
					<div class="flex flex-col items-center gap-8 text-center px-8">
						<p class="text-paragraph-size-lg">
							Kies voor een gezonde, vitale en energieke zwangerschap.
						</p>
						<AtomButton
							:to="{ url: '#aanmelden', title: 'Aanmeldformulier' }"
							text="MamaFit informatiepakket aanvragen"
							size="xl"
						/>
					</div>
				</div>
			</div>

			<div class="container flex flex-col gap-32 md:gap-64 md:pb-32">
				<div class="flex flex-col gap-12 items-center">
					<AtomTitle
						tag="h2"
						size="3xl"
						class="text-center"
					>
						Onze leden aan het woord
					</AtomTitle>

					<OrganismReviewSlideshow :reviews="reviews" />
				</div>
			</div>

			<div class="container max-w-screen-md flex flex-col gap-12 pb-36">
				<AtomTitle
					tag="h2"
					size="3xl"
					class="text-center"
				>
					Veelgestelde vragen
				</AtomTitle>
				<OrganismFaq :items="mamaFitFaqItems" />
			</div>
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import { cdnUrl } from '#base/helpers/contentHelpers';
import reviews from '#fit/core/consts/reviews';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import { mamaFitFaqItems } from '#fit/core/consts/faqItems';
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { VideoColorEnum } from '#base/core/enums/VideoColorEnum';

useSeoMeta({
	title: 'Fit tijdens en na je zwangerschap | Fit Hier',
	ogTitle: 'Fit tijdens en na je zwangerschap met Fit Hier',
	description: 'Werk aan een gezonde conditie gedurende én na je zwangerschap. Bouw veilig aan kracht en conditie om klachten te voorkomen en als voorheen te vertrouwen op je lichaam.',
});

const breadcrumbs: BreadcrumbsType = [
	{
		text: 'Home',
		to: {
			title: 'Home',
			url: '/',
		},
	},
	{
		text: 'MamaFit',
		to: {
			url: '/mamafit',
			title: 'MamaFit',
		},
	},
];
</script>
